/**
 * FontStyleSection -- 폰트 및 스타일 설정
 */

import { useMemo } from 'react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { Select } from '../../ui/index.js'
import styles from './SectionForm.module.css'

const FONT_OPTIONS = [
  { value: 'pretendard', label: 'Pretendard' },
  { value: 'noto-sans', label: 'Noto Sans KR' },
  { value: 'noto-serif', label: 'Noto Serif KR' },
  { value: 'gowun-batang', label: '고운바탕' },
  { value: 'nanum-myeongjo', label: '나눔명조' },
  { value: 'kopub-batang', label: 'KoPub 바탕' },
]

const TEXT_SCALE_OPTIONS = [
  { value: 'sm', label: '작게' },
  { value: 'md', label: '보통' },
  { value: 'lg', label: '크게' },
]

const FADE_SPEED_OPTIONS = [
  { value: 'slow', label: '느리게' },
  { value: 'normal', label: '보통' },
  { value: 'fast', label: '빠르게' },
  { value: 'none', label: '없음' },
]

interface ThemePreset {
  id: string
  name: string
  fontFamily: string
  backgroundColor: string
  textColor: string
}

const THEME_PRESETS: ThemePreset[] = [
  { id: 'classic', name: '클래식', fontFamily: 'nanum-myeongjo', backgroundColor: '#faf8f5', textColor: '#3d3530' },
  { id: 'modern', name: '모던', fontFamily: 'pretendard', backgroundColor: '#ffffff', textColor: '#1a1a1a' },
  { id: 'natural', name: '내추럴', fontFamily: 'gowun-batang', backgroundColor: '#f5f0eb', textColor: '#4a3f35' },
  { id: 'romantic', name: '로맨틱', fontFamily: 'noto-serif', backgroundColor: '#fdf5f5', textColor: '#5c3d3d' },
  { id: 'dark', name: '다크', fontFamily: 'pretendard', backgroundColor: '#2c2c2c', textColor: '#e8e4e0' },
]

export function FontStyleSection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  if (!invitation) return null

  const settings = (invitation as any).settings ?? {}

  const activePresetId = useMemo(() => {
    const font = settings.fontFamily ?? 'pretendard'
    const bg = settings.backgroundColor ?? '#ffffff'
    const text = settings.textColor
    const match = THEME_PRESETS.find(
      (p) => p.fontFamily === font && p.backgroundColor === bg && p.textColor === text,
    )
    return match?.id ?? null
  }, [settings.fontFamily, settings.backgroundColor, settings.textColor])

  const applyPreset = (preset: ThemePreset) => {
    updateNested('settings.fontFamily', preset.fontFamily)
    updateNested('settings.backgroundColor', preset.backgroundColor)
    updateNested('settings.textColor', preset.textColor)
  }

  return (
    <div className={styles.form}>
      {/* 테마 프리셋 */}
      <div className={styles.fieldGroup}>
        <div className={styles.fieldGroupTitle}>테마 프리셋</div>
        <div className={styles.themePresets}>
          {THEME_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`${styles.themeCard} ${activePresetId === preset.id ? styles.themeCardActive : ''}`}
              onClick={() => applyPreset(preset)}
            >
              <span
                className={styles.themePreview}
                style={{ backgroundColor: preset.backgroundColor }}
              >
                <span style={{ color: preset.textColor }}>가</span>
              </span>
              <span className={styles.themeName}>{preset.name}</span>
            </button>
          ))}
        </div>
      </div>

      <hr className={styles.themeDivider} />

      {/* 개별 설정 */}
      <Select
        label="폰트"
        value={settings.fontFamily ?? 'pretendard'}
        onChange={(e) => updateNested('settings.fontFamily', e.target.value)}
        options={FONT_OPTIONS}
      />
      <Select
        label="텍스트 크기"
        value={settings.textScale ?? 'md'}
        onChange={(e) => updateNested('settings.textScale', e.target.value)}
        options={TEXT_SCALE_OPTIONS}
      />
      <Select
        label="페이드인 효과"
        value={settings.fadeSpeed ?? 'normal'}
        onChange={(e) => updateNested('settings.fadeSpeed', e.target.value)}
        options={FADE_SPEED_OPTIONS}
      />
      <div className={styles.row}>
        <label className={styles.colorField}>
          <span className={styles.colorLabel}>배경 색상</span>
          <span className={styles.colorSwatch} style={{ backgroundColor: settings.backgroundColor ?? '#ffffff' }} />
          <input
            type="color"
            className={styles.colorInput}
            value={settings.backgroundColor ?? '#ffffff'}
            onChange={(e) => updateNested('settings.backgroundColor', e.target.value)}
          />
        </label>
        <label className={styles.colorField}>
          <span className={styles.colorLabel}>텍스트 색상</span>
          <span className={styles.colorSwatch} style={{ backgroundColor: settings.textColor ?? '#1a1a1a' }} />
          <input
            type="color"
            className={styles.colorInput}
            value={settings.textColor ?? '#1a1a1a'}
            onChange={(e) => updateNested('settings.textColor', e.target.value)}
          />
        </label>
      </div>
    </div>
  )
}
